import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { WordItem } from '../types';
import { playSound } from '../audio';
import { useKabyleTTS } from '../hooks/useKabyleTTS';
import { TTSButton } from './TTSButton';
import { formatPromptWithGrammar, getRelatedNumberForm, stripGrammarParentheses } from '../utils/kabyleGrammar';
import { parseConjugationPersons } from './ConjugationBox';
import { getWordIcon } from '../utils/emojis';
import { BerberRugBorder } from './Sprites';
import { CheckCircle, XCircle, Info, RotateCcw } from 'lucide-react';
import {
  getAllWordQuizStatuses,
  setWordQuizStatus,
  subscribeWordQuizStatuses,
  WordQuizStatus,
} from '../utils/quizWordStatusStorage';
import { clearActiveQuizId, setActiveQuizId } from '../utils/activeActivityManager';

export type QuizWorkingMode = 'WRITTEN' | 'ORAL';

export interface UnifiedQuizChallengeModalProps {
  challengeId?: string;
  title: string;
  subtitle?: string;
  icon?: string;
  words: WordItem[];
  allWords: WordItem[];
  onClose: () => void;
  onAddScore?: (xp: number) => void;
  initialHearts?: number;
  onToggleFavorite?: (word: WordItem) => void;
  isFavoriteMap?: Record<string, boolean>;
}

export interface OptionItem {
  text: string;
  word?: WordItem;
}

interface QuestionItem {
  targetWord: WordItem;
  mode: QuizWorkingMode;
  questionSubtype: 'KAB_TO_FR' | 'FR_TO_KAB' | 'AUDIO_TO_FR';
  prompt: string;
  promptSub: string;
  correctAnswer: string;
  options: OptionItem[];
}

const EMPTY_FAVORITE_MAP: Record<string, boolean> = {};

export const UnifiedQuizChallengeModal: React.FC<UnifiedQuizChallengeModalProps> = ({
  challengeId,
  title,
  subtitle,
  icon = '⚡',
  words,
  allWords,
  onClose,
  onAddScore,
  initialHearts = 5,
  onToggleFavorite,
  isFavoriteMap = EMPTY_FAVORITE_MAP,
}) => {
  const { playTTS } = useKabyleTTS();

  // Screen steps
  // Restore saved state
  const savedState = useMemo(() => {
    try {
      const raw = localStorage.getItem('tamazight_active_quiz_state_' + challengeId);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Ne restaurer que si une partie était en cours de jeu actif (PLAYING).
        // Si la partie précédente était terminée (VICTORY ou GAMEOVER), toujours repartir sur CONFIG !
        if (parsed && parsed.step === 'PLAYING') {
          return parsed;
        }
      }
    } catch {}
    return null;
  }, [challengeId]);

  const [step, setStep] = useState<'CONFIG' | 'PLAYING' | 'GAMEOVER' | 'VICTORY'>(() => savedState ? savedState.step : 'CONFIG');
  const [selectedMode, setSelectedMode] = useState<QuizWorkingMode>(() => savedState ? savedState.selectedMode : 'WRITTEN');
  const [questionCountMode, setQuestionCountMode] = useState<'ALL' | 5 | 10 | 20 | 'CUSTOM'>(() => savedState ? savedState.questionCountMode : 'ALL');
  const [customCountInput, setCustomCountInput] = useState<string>(() => savedState ? savedState.customCountInput : '');
  const [wordStatuses, setWordStatuses] = useState<Record<string, WordQuizStatus>>(() => getAllWordQuizStatuses());
  const [sessionWordResults, setSessionWordResults] = useState<Record<string, WordQuizStatus>>(() => savedState ? savedState.sessionWordResults : {});
  const [questions, setQuestions] = useState<QuestionItem[]>(() => savedState ? savedState.questions : []);
  const [currentIndex, setCurrentIndex] = useState(() => savedState ? savedState.currentIndex : 0);
  const [hearts, setHearts] = useState(() => savedState ? savedState.hearts : initialHearts);
  const [score, setScore] = useState(() => savedState ? savedState.score : 0);
  const [streak, setStreak] = useState(() => savedState ? savedState.streak : 0);
  const [maxStreak, setMaxStreak] = useState(() => savedState ? savedState.maxStreak : 0);
  const [mistakesCount, setMistakesCount] = useState(() => savedState ? savedState.mistakesCount : 0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(() => savedState ? savedState.selectedAnswer : null);
  const [isAnswerChecked, setIsAnswerChecked] = useState(() => savedState ? savedState.isAnswerChecked : false);
  const [showPlural, setShowPlural] = useState(() => savedState ? savedState.showPlural : false);
  const [showConjugationInfo, setShowConjugationInfo] = useState<boolean>(() => savedState ? (savedState.showConjugationInfo || false) : false);
  const [answersHistory, setAnswersHistory] = useState<Record<number, { selectedAnswer: string; isCorrect: boolean }>>(() => savedState?.answersHistory || {});
  const [maxReachedIndex, setMaxReachedIndex] = useState<number>(() => savedState?.maxReachedIndex ?? savedState?.currentIndex ?? 0);
  const advanceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pastillesContainerRef = useRef<HTMLDivElement | null>(null);
  const activeQuestionButtonRef = useRef<HTMLButtonElement | null>(null);

  // Défilement automatique fluide du carrousel de pagination pour centrer le numéro actif
  useEffect(() => {
    if (step === 'PLAYING') {
      const timer = setTimeout(() => {
        if (pastillesContainerRef.current && activeQuestionButtonRef.current) {
          const container = pastillesContainerRef.current;
          const target = activeQuestionButtonRef.current;
          const targetLeft = target.offsetLeft;
          const targetWidth = target.offsetWidth;
          const containerWidth = container.offsetWidth;

          container.scrollTo({
            left: Math.max(0, targetLeft - containerWidth / 2 + targetWidth / 2),
            behavior: 'smooth',
          });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, step]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, []);

  // Sync latest word pastilles whenever entering CONFIG step
  useEffect(() => {
    if (step === 'CONFIG') {
      setWordStatuses(getAllWordQuizStatuses());
    }
  }, [step]);

  
  const effectiveQuestionCount = useMemo(() => {
    if (questionCountMode === 'ALL') return words.length;
    if (questionCountMode === 'CUSTOM') return parseInt(customCountInput) || 5;
    return questionCountMode;
  }, [questionCountMode, words.length, customCountInput]);

  // Save state
  useEffect(() => {
    localStorage.setItem('tamazight_active_quiz_state_' + challengeId, JSON.stringify({
      step, selectedMode, questionCountMode, customCountInput, sessionWordResults,
      questions, currentIndex, hearts, score, streak, maxStreak, mistakesCount,
      selectedAnswer, isAnswerChecked, showPlural, showConjugationInfo, answersHistory, maxReachedIndex
    }));
  }, [challengeId, step, selectedMode, questionCountMode, customCountInput, sessionWordResults, questions, currentIndex, hearts, score, streak, maxStreak, mistakesCount, selectedAnswer, isAnswerChecked, showPlural, showConjugationInfo, answersHistory, maxReachedIndex]);

  // Recommencer et revenir aux paramètres de configuration du quizz
  const handleRestartToConfig = useCallback(() => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    playSound('click');
    setSelectedAnswer(null);
    setIsAnswerChecked(false);
    setShowPlural(false);
    setShowConjugationInfo(false);
    setAnswersHistory({});
    setMaxReachedIndex(0);
    setStep('CONFIG');
    if (challengeId) {
      clearActiveQuizId(challengeId);
      try {
        localStorage.removeItem('tamazight_active_quiz_state_' + challengeId);
      } catch {}
    }
  }, [challengeId]);

  // Start the quiz: picks the configured number of questions (default: all)
  
  const handleClose = () => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    if (step === 'GAMEOVER' || step === 'VICTORY' || step === 'CONFIG') {
      if (challengeId) {
        clearActiveQuizId(challengeId);
        try {
          localStorage.removeItem('tamazight_active_quiz_state_' + challengeId);
        } catch {}
      }
    }
    setStep('CONFIG');
    playSound('click');
    onClose();
  };

  const handleStartQuiz = useCallback(() => {
    if (words.length === 0) return;

    if (challengeId) {
      setActiveQuizId(challengeId);
    }

    playSound('click');
    setAnswersHistory({});
    setMaxReachedIndex(0);

    // Priorité de rejouabilité : 
    // 1. Mots ratés (pastille rouge / WRONG)
    // 2. Mots non découverts / non testés (pas de statut)
    // 3. Mots déjà validés (pastille verte / CORRECT)
    const currentStatuses = getAllWordQuizStatuses();
    setWordStatuses(currentStatuses);

    const wrongWords = words.filter((w) => currentStatuses[w.kab] === 'WRONG').sort(() => Math.random() - 0.5);
    const untestedWords = words.filter((w) => !currentStatuses[w.kab]).sort(() => Math.random() - 0.5);
    const correctWords = words.filter((w) => currentStatuses[w.kab] === 'CORRECT').sort(() => Math.random() - 0.5);

    const prioritizedWords = [...wrongWords, ...untestedWords, ...correctWords];
    const chosenWords = questionCountMode === 'ALL' ? prioritizedWords : prioritizedWords.slice(0, effectiveQuestionCount);

    const generated: QuestionItem[] = chosenWords.map((word) => {
      // Pick 3 distractors: STRICTEMENT de la même catégorie / liste de mots du défi
      const wordCat = word.type || word.category;
      
      // 1. Priorité absolue : les autres mots de la liste du quizz actuel
      let categoryPool = words.filter(
        (w) => w.kab !== word.kab && stripGrammarParentheses(w.fr) !== stripGrammarParentheses(word.fr)
      );

      // 2. Si la liste du quizz est petite (< 4 mots), compléter avec les mots de la même catégorie dans le dictionnaire
      if (categoryPool.length < 3 && wordCat) {
        const extraSameCategory = allWords.filter(
          (w) =>
            (w.type === wordCat || w.category === wordCat) &&
            w.kab !== word.kab &&
            stripGrammarParentheses(w.fr) !== stripGrammarParentheses(word.fr) &&
            !categoryPool.some((cp) => cp.kab === w.kab)
        );
        categoryPool = [...categoryPool, ...extraSameCategory];
      }

      // 3. Fallback ultime uniquement si la catégorie entière contient moins de 4 mots
      if (categoryPool.length < 3) {
        const fallback = allWords.filter(
          (w) =>
            w.kab !== word.kab &&
            stripGrammarParentheses(w.fr) !== stripGrammarParentheses(word.fr) &&
            !categoryPool.some((cp) => cp.kab === w.kab)
        );
        categoryPool = [...categoryPool, ...fallback];
      }

      const shuffledDistractors = [...categoryPool].sort(() => Math.random() - 0.5).slice(0, 3);

      if (selectedMode === 'ORAL') {
        // Pure oral listening question
        const options: OptionItem[] = [word, ...shuffledDistractors]
          .map((d) => ({
            text: stripGrammarParentheses(d.fr),
            word: d,
          }))
          .sort(() => Math.random() - 0.5);

        return {
          targetWord: word,
          mode: 'ORAL',
          questionSubtype: 'AUDIO_TO_FR',
          prompt: '🎧 Écoute bien...',
          promptSub: 'Quelle est la bonne signification en français ?',
          correctAnswer: stripGrammarParentheses(word.fr),
          options,
        };
      } else {
        // Pure written comprehension question (50% Kabyle->Fr, 50% Fr->Kabyle)
        const isKabToFr = Math.random() > 0.5;
        if (isKabToFr) {
          const options: OptionItem[] = [word, ...shuffledDistractors]
            .map((d) => ({
              text: stripGrammarParentheses(d.fr),
              word: d,
            }))
            .sort(() => Math.random() - 0.5);

          return {
            targetWord: word,
            mode: 'WRITTEN',
            questionSubtype: 'KAB_TO_FR',
            prompt: formatPromptWithGrammar(word, false),
            promptSub: 'Que signifie ce mot en Français ?',
            correctAnswer: stripGrammarParentheses(word.fr),
            options,
          };
        } else {
          // Mot à deviner en Kabyle
          const options: OptionItem[] = [word, ...shuffledDistractors]
            .map((d) => ({
              text: stripGrammarParentheses(d.kab),
              word: d,
            }))
            .sort(() => Math.random() - 0.5);

          return {
            targetWord: word,
            mode: 'WRITTEN',
            questionSubtype: 'FR_TO_KAB',
            prompt: formatPromptWithGrammar(word, true),
            promptSub: "Comment s'écrit ce mot en Kabyle ?",
            correctAnswer: stripGrammarParentheses(word.kab),
            options,
          };
        }
      }
    });

    setQuestions(generated);
    setCurrentIndex(0);
    setScore(0);
    setStreak(0);
    setMaxStreak(0);
    setMistakesCount(0);
    setSelectedAnswer(null);
    setIsAnswerChecked(false);
    setShowPlural(false);
    setShowConjugationInfo(false);
    setSessionWordResults({});
    setStep('PLAYING');

    // Auto-play audio immediately if oral mode
    if (selectedMode === 'ORAL' && generated[0]) {
      setTimeout(() => {
        playTTS(generated[0].targetWord.kab);
      }, 350);
    }
  }, [words, allWords, selectedMode, questionCountMode, effectiveQuestionCount, playTTS]);

  const currentQ = questions[currentIndex];

  const relatedNumberForm = useMemo(() => {
    if (!currentQ?.targetWord) return null;
    return getRelatedNumberForm(currentQ.targetWord, currentQ.questionSubtype === 'FR_TO_KAB');
  }, [currentQ]);

  // Auto-play audio when question changes in ORAL mode
  const currentOralKab = currentQ && currentQ.mode === 'ORAL' ? currentQ.targetWord.kab : null;
  useEffect(() => {
    if (step === 'PLAYING' && currentOralKab) {
      const timer = setTimeout(() => {
        playTTS(currentOralKab);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, step, currentOralKab, playTTS]);

  // Handle answering
  const handleSelectAnswer = (opt: string) => {
    if (isAnswerChecked || !currentQ) return;

    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }

    setSelectedAnswer(opt);
    setIsAnswerChecked(true);

    const isCorrect = opt === currentQ.correctAnswer;
    const wordKab = currentQ.targetWord.kab;
    const resultStatus: WordQuizStatus = isCorrect ? 'CORRECT' : 'WRONG';

    // Store question answer history
    setAnswersHistory((prev) => ({
      ...prev,
      [currentIndex]: { selectedAnswer: opt, isCorrect },
    }));
    setMaxReachedIndex((prev) => Math.max(prev, currentIndex));

    // Update session results and persistent storage immediately
    setSessionWordResults((prev) => ({
      ...prev,
      [wordKab]: resultStatus,
    }));
    setWordQuizStatus(wordKab, resultStatus);

    if (isCorrect) {
      playSound('correct');
      const newStreak = streak + 1;
      setStreak(newStreak);
      if (newStreak > maxStreak) setMaxStreak(newStreak);

      const xpGained = 50 + Math.min(newStreak * 10, 50);
      setScore((s) => s + xpGained);
      if (onAddScore) onAddScore(xpGained);

      // Fast & smooth advance on correct answer (1.2 seconds)
      advanceTimerRef.current = setTimeout(() => {
        advanceQuestion(mistakesCount);
      }, 1200);
    } else {
      playSound('wrong');
      setStreak(0);
      const newMistakes = mistakesCount + 1;
      setMistakesCount(newMistakes);
      // Balanced display time to assimilate the mistake and read the correct answer (2.4 seconds)
      advanceTimerRef.current = setTimeout(() => {
        advanceQuestion(newMistakes);
      }, 2400);
    }
  };

  const advanceQuestion = (currentMistakes: number = mistakesCount) => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }

    if (currentIndex + 1 >= questions.length) {
      playSound('victory');
      setStep('VICTORY');
      if (challengeId) {
        clearActiveQuizId(challengeId);
      }

      // Bonus de fin de défi Quizz
      const completionBonus = currentMistakes === 0 ? 500 : 250;
      setScore((s) => s + completionBonus);
      if (onAddScore) onAddScore(completionBonus);

      // Check if challenge is fully validated: TOUS les mots du défi ont une pastille verte (CORRECT)
      if (challengeId) {
        try {
          const freshStatuses = getAllWordQuizStatuses();
          const allGreen = words.length > 0 && words.every((w) => freshStatuses[w.kab] === 'CORRECT');

          if (allGreen) {
            const raw = localStorage.getItem('tamazight_quiz_perfect_challenges');
            const list: string[] = raw ? JSON.parse(raw) : [];
            if (!list.includes(challengeId)) {
              list.push(challengeId);
              localStorage.setItem('tamazight_quiz_perfect_challenges', JSON.stringify(list));
            }
          }
          window.dispatchEvent(new CustomEvent('tamazight_quiz_updated'));
          window.dispatchEvent(new CustomEvent('storage'));
        } catch {}
      }
    } else {
      const nextIdx = currentIndex + 1;
      setCurrentIndex(nextIdx);
      setMaxReachedIndex((prev) => Math.max(prev, nextIdx));
      setSelectedAnswer(null);
      setIsAnswerChecked(false);
      setShowPlural(false);
      setShowConjugationInfo(false);
    }
  };

  // Navigate to a specific question (e.g. previous question to review answer)
  const goToQuestion = (targetIdx: number) => {
    if (targetIdx < 0 || targetIdx >= questions.length) return;
    if (targetIdx > maxReachedIndex) return;

    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }

    playSound('click');
    setCurrentIndex(targetIdx);
    const recorded = answersHistory[targetIdx];
    if (recorded) {
      setSelectedAnswer(recorded.selectedAnswer);
      setIsAnswerChecked(true);
    } else {
      setSelectedAnswer(null);
      setIsAnswerChecked(false);
    }
    setShowPlural(false);
    setShowConjugationInfo(false);
  };

  // Count validated words in current list
  const validatedWordsCount = useMemo(() => {
    return words.filter((w) => wordStatuses[w.kab] === 'CORRECT').length;
  }, [words, wordStatuses]);

  const failedWordsCount = useMemo(() => {
    return words.filter((w) => wordStatuses[w.kab] === 'WRONG').length;
  }, [words, wordStatuses]);

  return (
    <div className="fixed inset-0 w-full h-[100dvh] bg-[#fbf7ee] berber-weave-pattern text-stone-900 flex flex-col z-[120] overflow-hidden select-none font-mono">
      {/* Top Rug Border */}
      <BerberRugBorder />

      {/* ========================================================= */}
      {/* HEADER (STANDARD RED MENU BUTTON + STATUS)                */}
      {/* ========================================================= */}
      <header className="pt-3 pb-2.5 px-3 sm:px-6 bg-[#fffdf9] border-b-2 border-[#b45309]/30 shadow-xs flex items-center justify-between gap-3 shrink-0 z-30">
        <button
          type="button"
          onClick={() => {
            handleClose();
          }}
          className="h-8 px-3.5 bg-[#fff1f2] hover:bg-[#ffe4e6] text-red-900 font-black text-xs uppercase tracking-wider border-2 border-red-300 rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
        >
          <svg className="w-3 h-3 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
            <polygon points="17,4 7,12 17,20" />
          </svg>
          <span>Retour</span>
        </button>

        {/* Right Header Status: Recommencer & XP */}
        <div className="flex items-center gap-2 shrink-0">
          {step !== 'CONFIG' && (
            <button
              type="button"
              onClick={handleRestartToConfig}
              className="h-8 px-2.5 sm:px-3 bg-amber-100/90 hover:bg-amber-200 text-amber-950 font-black text-xs uppercase tracking-wider border-2 border-amber-400/80 rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
              title="Recommencer et revenir aux paramètres du quizz"
            >
              <RotateCcw className="w-3.5 h-3.5 text-amber-900 shrink-0" />
              <span>Recommencer</span>
            </button>
          )}

          {/* XP */}
          <div className="h-8 bg-amber-50 border-2 border-amber-300 px-3 rounded-lg flex items-center gap-1.5 shadow-xs">
            <span className="text-[10px] text-amber-900 uppercase font-black tracking-wide">XP</span>
            <span className="text-amber-950 font-black text-xs font-mono">+{score}</span>
          </div>
        </div>
      </header>

      {/* ========================================================= */}
      {/* VIEW 1: PRE-LAUNCH CONFIGURATION SCREEN                   */}
      {/* ========================================================= */}
      {step === 'CONFIG' && (
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col justify-start max-w-xl w-full mx-auto gap-4">
          {/* Introduction Card */}
          <div className="bg-[#fffdf9] border-2 border-[#b45309]/40 rounded-2xl p-3 sm:p-4 shadow-sm text-center">
            <div className="text-2xl sm:text-3xl mb-0.5">{icon}</div>
            <h2 className="text-base sm:text-lg font-black text-amber-950 uppercase tracking-wider font-cinzel">
              {title}
            </h2>
            {subtitle && (
              <p className="text-xs text-stone-600 font-mono mt-0.5">{subtitle}</p>
            )}
            
            {/* Rule highlight banner */}
            <div className="mt-2.5 p-2.5 bg-amber-50/90 border border-amber-300/80 rounded-xl text-left text-xs leading-relaxed text-amber-950">
              <div className="font-black flex items-center gap-1.5 text-amber-900 uppercase tracking-wide">
                <span>🎯</span>
                <span>Règle de validation intégrale :</span>
              </div>
              <p className="mt-1 text-[11px] text-amber-900/90">
                Pour valider ce quizz, <strong>tous les {words.length} mots doivent avoir une pastille verte (🟢)</strong>. Si vous jouez étape par étape (ex. 10 mots par 10), les mots ratés (🔴) reviennent en priorité, suivis des mots non découverts, jusqu'à la réussite complète !
              </p>
            </div>
          </div>

          {/* Mode Selection Cards */}
          <div className="space-y-2">
            <label className="text-xs font-black uppercase tracking-wider text-amber-900 block font-cinzel">
              1. Mode de Travail :
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
              {/* Mode 1: Written */}
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setSelectedMode('WRITTEN');
                }}
                className={`p-3 sm:p-3.5 rounded-xl border-2 text-left transition-all cursor-pointer flex flex-col justify-between gap-1.5 ${
                  selectedMode === 'WRITTEN'
                    ? 'bg-[#fef9ee] border-amber-600 shadow-sm ring-2 ring-amber-500/30'
                    : 'bg-[#fffdf9] border-stone-200 hover:border-amber-400 opacity-90'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xl sm:text-2xl">📖</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                    selectedMode === 'WRITTEN' ? 'bg-amber-600 text-white' : 'bg-stone-100 text-stone-600'
                  }`}>
                    {selectedMode === 'WRITTEN' ? 'Sélectionné' : 'Choisir'}
                  </span>
                </div>
                <div>
                  <h3 className="text-sm font-black text-amber-950 uppercase tracking-wide">
                    Compréhension Écrite
                  </h3>
                  <p className="text-xs text-stone-600 mt-0.5 leading-snug">
                    Lecture et traduction écrite de tous les mots.
                  </p>
                </div>
              </button>

              {/* Mode 2: Oral */}
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setSelectedMode('ORAL');
                }}
                className={`p-3 sm:p-3.5 rounded-xl border-2 text-left transition-all cursor-pointer flex flex-col justify-between gap-1.5 ${
                  selectedMode === 'ORAL'
                    ? 'bg-[#fef9ee] border-amber-600 shadow-sm ring-2 ring-amber-500/30'
                    : 'bg-[#fffdf9] border-stone-200 hover:border-amber-400 opacity-90'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xl sm:text-2xl">🎧</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                    selectedMode === 'ORAL' ? 'bg-amber-600 text-white' : 'bg-stone-100 text-stone-600'
                  }`}>
                    {selectedMode === 'ORAL' ? 'Sélectionné' : 'Choisir'}
                  </span>
                </div>
                <div>
                  <h3 className="text-sm font-black text-amber-950 uppercase tracking-wide">
                    Compréhension Orale
                  </h3>
                  <p className="text-xs text-stone-600 mt-0.5 leading-snug">
                    Écoute audio et mot masqué.
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* Section 2: Question Count Configuration */}
          <div className="space-y-2">
            <label className="text-xs font-black uppercase tracking-wider text-amber-900 block font-cinzel">
              2. Nombre de questions :
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {/* Option: Toutes (Défaut) */}
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setQuestionCountMode('ALL');
                }}
                className={`p-2.5 rounded-xl border-2 text-center transition-all cursor-pointer flex flex-col items-center justify-center gap-0.5 ${
                  questionCountMode === 'ALL'
                    ? 'bg-amber-100 border-amber-600 text-amber-950 font-black shadow-xs ring-2 ring-amber-500/30'
                    : 'bg-[#fffdf9] border-stone-200 text-stone-700 hover:border-amber-400 font-bold'
                }`}
              >
                <span className="text-xs uppercase tracking-wide">Toutes</span>
                <span className="text-[10px] opacity-80">({words.length} mots)</span>
              </button>

              {/* Option: 5 questions */}
              <button
                type="button"
                disabled={words.length < 5}
                onClick={() => {
                  playSound('click');
                  setQuestionCountMode(5);
                }}
                className={`p-2.5 rounded-xl border-2 text-center transition-all flex flex-col items-center justify-center gap-0.5 ${
                  words.length < 5
                    ? 'bg-stone-100 border-stone-200 text-stone-400 opacity-50 cursor-not-allowed'
                    : questionCountMode === 5
                    ? 'bg-amber-100 border-amber-600 text-amber-950 font-black shadow-xs ring-2 ring-amber-500/30 cursor-pointer'
                    : 'bg-[#fffdf9] border-stone-200 text-stone-700 hover:border-amber-400 font-bold cursor-pointer'
                }`}
              >
                <span className="text-xs uppercase tracking-wide">5 questions</span>
                <span className="text-[10px] opacity-80">Rapide ⚡</span>
              </button>

              {/* Option: 10 questions */}
              <button
                type="button"
                disabled={words.length < 10}
                onClick={() => {
                  playSound('click');
                  setQuestionCountMode(10);
                }}
                className={`p-2.5 rounded-xl border-2 text-center transition-all flex flex-col items-center justify-center gap-0.5 ${
                  words.length < 10
                    ? 'bg-stone-100 border-stone-200 text-stone-400 opacity-50 cursor-not-allowed'
                    : questionCountMode === 10
                    ? 'bg-amber-100 border-amber-600 text-amber-950 font-black shadow-xs ring-2 ring-amber-500/30 cursor-pointer'
                    : 'bg-[#fffdf9] border-stone-200 text-stone-700 hover:border-amber-400 font-bold cursor-pointer'
                }`}
              >
                <span className="text-xs uppercase tracking-wide">10 questions</span>
                <span className="text-[10px] opacity-80">Standard 🎯</span>
              </button>

              {/* Option: 20 questions */}
              <button
                type="button"
                disabled={words.length < 20}
                onClick={() => {
                  playSound('click');
                  setQuestionCountMode(20);
                }}
                className={`p-2.5 rounded-xl border-2 text-center transition-all flex flex-col items-center justify-center gap-0.5 ${
                  words.length < 20
                    ? 'bg-stone-100 border-stone-200 text-stone-400 opacity-50 cursor-not-allowed'
                    : questionCountMode === 20
                    ? 'bg-amber-100 border-amber-600 text-amber-950 font-black shadow-xs ring-2 ring-amber-500/30 cursor-pointer'
                    : 'bg-[#fffdf9] border-stone-200 text-stone-700 hover:border-amber-400 font-bold cursor-pointer'
                }`}
              >
                <span className="text-xs uppercase tracking-wide">20 questions</span>
                <span className="text-[10px] opacity-80">Avancé 🏆</span>
              </button>
            </div>

            {/* Option: Champ Personnalisé */}
            <div className={`p-2.5 rounded-xl border-2 transition-all flex items-center justify-between gap-3 ${
              questionCountMode === 'CUSTOM'
                ? 'bg-amber-50 border-amber-600 shadow-xs ring-1 ring-amber-500/30'
                : 'bg-[#fffdf9] border-stone-200 hover:border-amber-300'
            }`}>
              <div 
                className="flex items-center gap-2 cursor-pointer flex-1"
                onClick={() => {
                  playSound('click');
                  setQuestionCountMode('CUSTOM');
                  if (!customCountInput) {
                    setCustomCountInput(Math.min(10, words.length).toString());
                  }
                }}
              >
                <span className="text-xs font-bold text-amber-950 cursor-pointer flex items-center gap-1.5">
                  <span>✏️</span>
                  <span>Nombre personnalisé :</span>
                </span>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max={words.length}
                  value={customCountInput}
                  placeholder={Math.min(10, words.length).toString()}
                  onFocus={() => {
                    setQuestionCountMode('CUSTOM');
                  }}
                  onChange={(e) => {
                    setQuestionCountMode('CUSTOM');
                    setCustomCountInput(e.target.value);
                  }}
                  className="w-20 px-2.5 py-1 text-center font-black text-sm bg-white border-2 border-amber-400 rounded-lg text-amber-950 shadow-inner focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <span className="text-xs text-stone-500">/ {words.length} max</span>
              </div>
            </div>
          </div>

          {/* Section 3: Aperçu et Pastilles des mots de la liste */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black uppercase tracking-wider text-amber-900 block font-cinzel">
                3. Liste des mots & pastilles ({words.length} mots) :
              </label>
              <div className="flex items-center gap-2 text-[10px] text-stone-600">
                <span className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" /> Validé
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-rose-500" /> À revoir
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-stone-300" /> Non testé
                </span>
              </div>
            </div>

            <div className="bg-[#fffdf9] border-2 border-stone-200 rounded-xl p-3 max-h-48 overflow-y-auto space-y-1.5 shadow-2xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {words.map((w, idx) => {
                  const status = wordStatuses[w.kab];
                  return (
                    <div
                      key={idx}
                      className={`p-2 rounded-lg border flex items-center justify-between text-xs transition-colors ${
                        status === 'CORRECT'
                          ? 'bg-emerald-50/80 border-emerald-300 text-emerald-950'
                          : status === 'WRONG'
                          ? 'bg-rose-50/80 border-rose-300 text-rose-950'
                          : 'bg-[#fcf8f0] border-stone-200 text-stone-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 truncate min-w-0">
                        <span
                          className={`w-3 h-3 rounded-full shrink-0 flex items-center justify-center text-[8px] font-black ${
                            status === 'CORRECT'
                              ? 'bg-emerald-500 text-white shadow-[0_0_6px_#10b981]'
                              : status === 'WRONG'
                              ? 'bg-rose-500 text-white shadow-[0_0_6px_#f43f5e]'
                              : 'bg-stone-300 text-stone-600'
                          }`}
                        >
                          {status === 'CORRECT' ? '✓' : status === 'WRONG' ? '✕' : ''}
                        </span>
                        <span className="font-bold truncate">{w.kab}</span>
                      </div>
                      <span className="text-[11px] text-stone-500 italic truncate ml-1">{w.fr}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Launch Button: Launches configured question count */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleStartQuiz}
              className="w-full py-3.5 sm:py-4 bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 hover:from-amber-500 hover:to-yellow-400 text-stone-950 font-black text-sm sm:text-base uppercase tracking-widest rounded-xl shadow-md border-2 border-amber-400 active:scale-98 transition-all cursor-pointer flex items-center justify-center gap-2"
            >
              <span>⚡</span>
              <span>
                Lancer le Quizz {questionCountMode === 'ALL' ? `Complet (${effectiveQuestionCount} questions)` : `(${effectiveQuestionCount} questions)`}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* VIEW 2: ACTIVE QUESTION SCREEN                            */}
      {/* ========================================================= */}
      {step === 'PLAYING' && currentQ && (
        <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5 pb-10 flex flex-col justify-start max-w-xl w-full mx-auto gap-3">
          {/* Top Word Progress Pastilles Bar */}
          <div className="bg-[#fffdf9] border-2 border-[#b45309]/30 rounded-xl p-2.5 shadow-2xs shrink-0 flex flex-col gap-2">
            <div className="flex items-center justify-between text-xs text-stone-700 font-bold">
              {/* Interactive question counter / previous question button */}
              <button
                type="button"
                onClick={() => {
                  if (currentIndex > 0) {
                    goToQuestion(currentIndex - 1);
                  }
                }}
                disabled={currentIndex === 0}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-all ${
                  currentIndex > 0
                    ? 'bg-amber-100 hover:bg-amber-200 border-amber-300 text-amber-950 cursor-pointer active:scale-95 shadow-2xs'
                    : 'bg-stone-100 border-stone-200 text-stone-600 opacity-80 cursor-default'
                }`}
                title={currentIndex > 0 ? 'Toucher pour revenir à la question précédente' : 'Première question'}
              >
                {currentIndex > 0 && <span className="text-amber-800 text-xs">◀</span>}
                <span>Question</span>
                <span className="text-amber-950 font-black text-sm">{currentIndex + 1}</span>
                <span className="text-stone-400">/</span>
                <span>{questions.length}</span>
              </button>

              <div className="flex items-center gap-1.5">
                {currentIndex < maxReachedIndex && (
                  <button
                    type="button"
                    onClick={() => goToQuestion(currentIndex + 1)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-[11px] font-black cursor-pointer shadow-2xs active:scale-95"
                    title="Revenir à la question suivante"
                  >
                    <span>Suivante</span>
                    <span>▶</span>
                  </button>
                )}

                {currentQ.mode === 'ORAL' && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-950 font-bold uppercase">
                    🎧 Écoute
                  </span>
                )}
              </div>
            </div>

            {/* Scannable horizontal pastilles bar (clickable for past/reached questions) with auto-scrolling */}
            <div
              ref={pastillesContainerRef}
              className="flex items-center gap-1.5 overflow-x-auto py-1.5 px-1 no-scrollbar scroll-smooth"
            >
              {questions.map((q, qIdx) => {
                const qKab = q.targetWord.kab;
                const status = sessionWordResults[qKab] || (qIdx < currentIndex ? wordStatuses[qKab] : null);
                const isCurrent = qIdx === currentIndex;
                const isAccessible = qIdx <= maxReachedIndex;

                return (
                  <button
                    key={qIdx}
                    ref={isCurrent ? activeQuestionButtonRef : null}
                    type="button"
                    disabled={!isAccessible}
                    onClick={() => {
                      if (isAccessible) {
                        goToQuestion(qIdx);
                      }
                    }}
                    title={`${qIdx + 1}. ${qKab} (${status === 'CORRECT' ? 'Validé' : status === 'WRONG' ? 'Raté' : 'À venir'})${isAccessible ? ' • Toucher pour voir' : ''}`}
                    className={`h-7.5 sm:h-8 min-w-[28px] sm:min-w-[32px] px-1.5 rounded-lg text-xs font-black flex items-center justify-center transition-all shrink-0 shadow-2xs ${
                      isAccessible ? 'cursor-pointer hover:scale-105 active:scale-95' : 'cursor-default opacity-60'
                    } ${
                      status === 'CORRECT'
                        ? 'bg-emerald-500 text-white shadow-[0_0_6px_#10b981]'
                        : status === 'WRONG'
                        ? 'bg-rose-500 text-white shadow-[0_0_6px_#f43f5e]'
                        : isCurrent
                        ? 'bg-amber-400 text-stone-950 ring-2 ring-amber-600 ring-offset-1 animate-pulse'
                        : 'bg-stone-200 text-stone-600 border border-stone-300'
                    }`}
                  >
                    {status === 'CORRECT' ? '✓' : status === 'WRONG' ? '✕' : qIdx + 1}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Question Prompt Card */}
          <div className="bg-[#fffdf9] border-2 border-[#b45309]/30 rounded-2xl p-4 sm:p-5 flex flex-col items-center justify-center text-center shadow-xs shrink-0 w-full relative min-h-[140px]">
            <div className="text-xs text-stone-500 font-semibold mb-1 uppercase tracking-wider">
              {currentQ.promptSub}
            </div>

            {currentQ.mode === 'ORAL' ? (
              /* ORAL MODE: Large audio listening button */
              <div className="flex flex-col items-center gap-2.5 my-2">
                <button
                  type="button"
                  onClick={() => {
                    playSound('click');
                    playTTS(currentQ.targetWord.kab);
                  }}
                  className="w-18 h-18 sm:w-20 sm:h-20 rounded-full bg-gradient-to-tr from-amber-500 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-stone-950 border-4 border-amber-200 flex items-center justify-center text-3xl shadow-md active:scale-95 transition-all cursor-pointer animate-pulse"
                  title="Réécouter la voix"
                >
                  🔊
                </button>
                <span className="text-xs text-amber-900 font-bold uppercase tracking-wider">
                  ✦ Touche pour réécouter ✦
                </span>
                {/* Revealed word after answer is checked */}
                {isAnswerChecked && (
                  <div className="mt-1 text-sm text-amber-950 font-bold flex flex-col items-center justify-center gap-2">
                    <div>
                      Mot prononcé : <span className="text-amber-900 font-black">{currentQ.targetWord.kab}</span>
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-2 mt-0.5">
                      {relatedNumberForm && (
                        <div>
                          {!showPlural ? (
                            <button
                              type="button"
                              onClick={() => {
                                playSound('click');
                                setShowPlural(true);
                              }}
                              className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-amber-950 bg-amber-100 hover:bg-amber-200 border border-dashed border-amber-400 px-3.5 py-2 rounded-xl cursor-pointer transition-all active:scale-95 shadow-2xs group min-h-[38px]"
                              title="Toucher pour révéler le pluriel"
                            >
                              <span>📚</span>
                              <span className="font-semibold text-amber-900">Afficher le pluriel</span>
                              <span className="text-xs text-amber-700 opacity-80 group-hover:opacity-100">👆</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                playSound('click');
                                setShowPlural(false);
                              }}
                              className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-amber-950 bg-amber-200 border-2 border-amber-500 px-3.5 py-2 rounded-xl cursor-pointer transition-all active:scale-95 shadow-xs animate-in fade-in min-h-[38px]"
                              title="Toucher pour masquer"
                            >
                              <span>📚</span>
                              <span>{relatedNumberForm.display}</span>
                              <span className="text-xs text-amber-800 ml-1">✕</span>
                            </button>
                          )}
                        </div>
                      )}

                      {/* Information "i" Conjugaison (Accompli & Inaccompli) en mode oral révélé */}
                      {(currentQ.targetWord.conjugationAccompli || currentQ.targetWord.conjugationInaccompli) && (
                        <button
                          type="button"
                          onClick={() => {
                            playSound('click');
                            setShowConjugationInfo(true);
                          }}
                          className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-blue-950 bg-blue-50 hover:bg-blue-100 border-2 border-blue-300 hover:border-blue-400 px-3.5 py-2 rounded-xl shadow-2xs active:scale-95 transition-all cursor-pointer min-h-[38px]"
                          title="Voir les formes de conjugaison"
                        >
                          <span className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-black italic">
                            i
                          </span>
                          <span>Conjugaison</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* WRITTEN MODE: Display word */
              <div className="my-1.5 flex flex-col items-center w-full">
                <div className="inline-flex flex-wrap items-center justify-center gap-2">
                  {currentQ.questionSubtype === 'FR_TO_KAB' && getWordIcon(currentQ.targetWord) && (
                    <span className="text-3xl sm:text-4xl animate-bounce" aria-hidden="true">
                      {getWordIcon(currentQ.targetWord)}
                    </span>
                  )}
                  <h2 className="text-2xl sm:text-3xl font-black text-amber-950 tracking-wide font-cinzel leading-tight">
                    « {currentQ.prompt} »
                  </h2>
                </div>

                {/* Helper action badges row (Pluriel, Conjugaison, Audio) */}
                <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                  {/* Encadré interactif pour révéler le pluriel sur appui */}
                  {relatedNumberForm && (
                    <div>
                      {!showPlural ? (
                        <button
                          type="button"
                          onClick={() => {
                            playSound('click');
                            setShowPlural(true);
                          }}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-950 bg-amber-100/95 hover:bg-amber-200 border-2 border-dashed border-amber-400/80 hover:border-amber-500 px-3 py-1 rounded-xl shadow-2xs active:scale-95 transition-all cursor-pointer group"
                          title="Toucher pour révéler le pluriel"
                        >
                          <span>📚</span>
                          <span className="font-semibold text-amber-900">Afficher le pluriel</span>
                          <span className="text-xs text-amber-700 opacity-80 group-hover:opacity-100">👆</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            playSound('click');
                            setShowPlural(false);
                          }}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-950 bg-amber-200/95 hover:bg-amber-300 border-2 border-amber-500 px-3 py-1 rounded-xl shadow-xs active:scale-95 transition-all cursor-pointer animate-in fade-in"
                          title="Toucher pour masquer"
                        >
                          <span>📚</span>
                          <span className="font-black text-amber-950">{relatedNumberForm.display}</span>
                          <span className="text-[11px] text-amber-800 ml-1 font-sans">✕</span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Bouton Info "i" interactif pour ouvrir le panneau de conjugaison */}
                  {(currentQ.targetWord.conjugationAccompli || currentQ.targetWord.conjugationInaccompli) && (
                    <button
                      type="button"
                      onClick={() => {
                        playSound('click');
                        setShowConjugationInfo(true);
                      }}
                      className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-blue-950 bg-blue-50 hover:bg-blue-100 border-2 border-blue-300 hover:border-blue-400 px-3.5 py-2 rounded-xl shadow-2xs active:scale-95 transition-all cursor-pointer min-h-[38px]"
                      title="Toucher pour voir les formes de conjugaison (Accompli & Inaccompli)"
                    >
                      <span className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-black italic shrink-0">
                        i
                      </span>
                      <span>Conjugaison</span>
                    </button>
                  )}

                  {/* Audio button for written mode */}
                  {(currentQ.questionSubtype === 'KAB_TO_FR' || isAnswerChecked) && (
                    <TTSButton
                      text={currentQ.targetWord.kab}
                      label="Écouter"
                      className="bg-amber-100 hover:bg-amber-200 border-2 border-amber-300 text-amber-950 text-xs sm:text-sm py-1.5 px-3.5 rounded-xl min-h-[38px]"
                    />
                  )}
                </div>

                {currentQ.targetWord.phonetic && (
                  <p className="text-xs text-stone-500 italic mt-1.5 font-sans">
                    [{currentQ.targetWord.phonetic}]
                  </p>
                )}
              </div>
            )}
          </div>

          {/* 4 Choices */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 shrink-0 pt-1 w-full">
            {currentQ.options.map((opt, i) => {
              const isCorrectOpt = opt.text === currentQ.correctAnswer;
              const isSelectedOpt = opt.text === selectedAnswer;

              let btnStyle =
                'bg-[#fffdf9] hover:bg-[#fef3c7] text-stone-900 border-[#e2d6c1] hover:border-amber-600 shadow-xs';

              if (isAnswerChecked) {
                if (isCorrectOpt) {
                  btnStyle =
                    'bg-emerald-100 text-emerald-950 border-emerald-500 ring-2 ring-emerald-400 shadow-md scale-[1.01]';
                } else if (isSelectedOpt) {
                  btnStyle = 'bg-rose-100 text-rose-950 border-rose-400 opacity-90';
                } else {
                  btnStyle = 'bg-stone-100 text-stone-400 border-stone-200 opacity-40';
                }
              }

              const optLen = opt.text.length;
              const fontSizeClass =
                optLen > 30
                  ? 'text-[11px] sm:text-xs md:text-sm leading-tight'
                  : optLen > 18
                  ? 'text-xs sm:text-sm md:text-base leading-snug'
                  : 'text-sm sm:text-base leading-tight';

              return (
                <button
                  key={i}
                  type="button"
                  disabled={isAnswerChecked}
                  onClick={() => handleSelectAnswer(opt.text)}
                  className={`p-2.5 sm:p-3.5 text-center font-black ${fontSizeClass} uppercase tracking-wide rounded-xl border-2 active:translate-y-0.5 transition-all cursor-pointer min-h-[56px] flex flex-col items-center justify-center relative ${btnStyle}`}
                >
                  <span className="max-w-full px-2 break-words">{opt.text}</span>
                  {isAnswerChecked && isCorrectOpt && (
                    <span className="text-xs font-black uppercase text-emerald-800 bg-emerald-200/90 px-2.5 py-0.5 rounded-md mt-1 border border-emerald-400">
                      ✓ Bonne réponse
                    </span>
                  )}
                  {isAnswerChecked && isSelectedOpt && !isCorrectOpt && (
                    <span className="text-xs font-black uppercase text-rose-800 bg-rose-200/90 px-2.5 py-0.5 rounded-md mt-1 border border-rose-400">
                      ✕ Votre choix
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* DEDICATED CONJUGATION MODAL / POPUP DIALOG */}
          {showConjugationInfo && (currentQ.targetWord.conjugationAccompli || currentQ.targetWord.conjugationInaccompli) && (
            <div
              className="fixed inset-0 bg-stone-950/60 z-[150] flex items-center justify-center p-4 backdrop-blur-xs animate-in fade-in"
              onClick={() => setShowConjugationInfo(false)}
            >
              <div
                className="bg-[#fffdf9] border-2 border-amber-600/80 rounded-2xl max-w-md w-full p-4 sm:p-5 shadow-2xl space-y-3.5 relative"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-amber-200 pb-2.5">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-black italic">
                      i
                    </span>
                    <div>
                      <h3 className="text-sm font-black text-amber-950 uppercase tracking-wide">
                        Conjugaison • {currentQ.targetWord.kab}
                      </h3>
                      <p className="text-[11px] text-stone-500 italic">
                        {currentQ.targetWord.fr}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowConjugationInfo(false)}
                    className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center justify-center font-bold text-sm cursor-pointer"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-2.5">
                  {/* Inaccompli d'abord */}
                  {currentQ.targetWord.conjugationInaccompli && (() => {
                    const parsed = parseConjugationPersons(currentQ.targetWord.conjugationInaccompli, 'inaccompli');
                    return (
                      <div className="bg-blue-50/90 rounded-xl p-3 border-2 border-blue-300 shadow-2xs space-y-1.5">
                        <div className="text-[11px] font-black text-blue-900 uppercase tracking-wider flex items-center justify-between">
                          <span>🔄 Inaccompli :</span>
                          <TTSButton
                            text={(parsed?.firstPerson || currentQ.targetWord.conjugationInaccompli).replace(/\([^)]*\)/g, '').trim()}
                            label="Écouter"
                            className="bg-blue-200 hover:bg-blue-300 border border-blue-400 text-blue-950 text-xs py-0.5 px-2 shrink-0"
                          />
                        </div>
                        <div className="text-sm font-bold text-stone-900 flex flex-wrap items-center gap-2">
                          <span className="text-blue-950 font-black">{parsed?.firstPerson}</span>
                          {parsed?.thirdPerson && (
                            <>
                              <span className="text-blue-400 font-normal">|</span>
                              <span className="text-stone-800 font-semibold">{parsed.thirdPerson}</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Accompli ensuite */}
                  {currentQ.targetWord.conjugationAccompli && (() => {
                    const parsed = parseConjugationPersons(currentQ.targetWord.conjugationAccompli, 'accompli');
                    return (
                      <div className="bg-amber-50/90 rounded-xl p-3 border-2 border-amber-300 shadow-2xs space-y-1.5">
                        <div className="text-[11px] font-black text-amber-900 uppercase tracking-wider flex items-center justify-between">
                          <span>⏳ Accompli :</span>
                          <TTSButton
                            text={(parsed?.firstPerson || currentQ.targetWord.conjugationAccompli).replace(/\([^)]*\)/g, '').trim()}
                            label="Écouter"
                            className="bg-amber-200 hover:bg-amber-300 border border-amber-400 text-amber-950 text-xs py-0.5 px-2 shrink-0"
                          />
                        </div>
                        <div className="text-sm font-bold text-stone-900 flex flex-wrap items-center gap-2">
                          <span className="text-amber-950 font-black">{parsed?.firstPerson}</span>
                          {parsed?.thirdPerson && (
                            <>
                              <span className="text-amber-400 font-normal">|</span>
                              <span className="text-stone-800 font-semibold">{parsed.thirdPerson}</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => setShowConjugationInfo(false)}
                    className="w-full py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-xs cursor-pointer active:scale-98 transition-all"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* VIEW 3: VICTORY SCREEN                                    */}
      {/* ========================================================= */}
      {step === 'VICTORY' && (
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col items-center justify-start text-center gap-3.5 max-w-lg w-full mx-auto">
          <div className="text-4xl sm:text-5xl animate-bounce mt-2">{mistakesCount === 0 ? '🌟' : '🏆'}</div>
          
          <div>
            <h2 className="text-xl sm:text-2xl font-black text-amber-950 uppercase tracking-wider font-cinzel">
              {mistakesCount === 0 ? 'Quizz 100% Validé !' : 'Défi Terminé'}
            </h2>
            <p className="text-xs text-stone-700 max-w-xs leading-relaxed mt-0.5">
              Mode <strong className="text-amber-900">{selectedMode === 'ORAL' ? 'Compréhension Orale' : 'Compréhension Écrite'}</strong>
            </p>
          </div>

          {/* Validation banner */}
          {(() => {
            const freshStatuses = getAllWordQuizStatuses();
            const totalWordsCount = words.length;
            const totalGreenCount = words.filter((w) => freshStatuses[w.kab] === 'CORRECT').length;
            const isEntireChallengeValidated = totalWordsCount > 0 && totalGreenCount === totalWordsCount;

            if (isEntireChallengeValidated) {
              return (
                <div className="bg-emerald-50 border-2 border-emerald-500 rounded-xl p-3 w-full shadow-xs text-emerald-950 flex items-center gap-2.5">
                  <span className="text-2xl shrink-0">✨</span>
                  <div className="text-left text-xs leading-snug">
                    <p className="font-black uppercase tracking-wide text-emerald-900">
                      Défi Entièrement Validé ({totalGreenCount}/{totalWordsCount} Pastilles Vertes)
                    </p>
                    <p className="text-emerald-800 text-[11px] mt-0.5">
                      Félicitations ! <strong>La totalité des mots du défi</strong> possède désormais une pastille verte (🟢). Le défi est validé !
                    </p>
                  </div>
                </div>
              );
            }

            return (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-3 w-full text-stone-800 text-xs text-left">
                <div className="flex items-center gap-1.5 font-bold text-amber-950">
                  <span>ℹ️</span>
                  <span>Progression globale : {totalGreenCount}/{totalWordsCount} mots validés (🟢)</span>
                </div>
                <p className="text-[11px] text-amber-900/90 mt-1 leading-relaxed">
                  Pour valider définitivement ce quizz, <strong>tous les {totalWordsCount} mots doivent avoir une pastille verte (🟢)</strong>. En rejouant, les mots ratés ou non découverts vous seront proposés en priorité !
                </p>
              </div>
            );
          })()}

          {/* Summary word list with green/red pastilles */}
          <div className="w-full bg-[#fffdf9] border-2 border-stone-200 rounded-xl p-3 text-left space-y-2 shadow-2xs">
            <div className="flex items-center justify-between text-xs font-black uppercase text-amber-950">
              <span>Bilan des Mots ({questions.length}) :</span>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-emerald-700 font-bold">
                  🟢 {Object.values(sessionWordResults).filter((s) => s === 'CORRECT').length} validés
                </span>
                {mistakesCount > 0 && (
                  <span className="text-rose-700 font-bold">
                    🔴 {Object.values(sessionWordResults).filter((s) => s === 'WRONG').length} ratés
                  </span>
                )}
              </div>
            </div>

            <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
              {questions.map((q, idx) => {
                const word = q.targetWord;
                const status = sessionWordResults[word.kab] || (mistakesCount === 0 ? 'CORRECT' : wordStatuses[word.kab]);
                const isPassed = status === 'CORRECT';

                return (
                  <div
                    key={idx}
                    className={`p-2 rounded-lg border flex items-center justify-between text-xs ${
                      isPassed
                        ? 'bg-emerald-50/90 border-emerald-300 text-emerald-950'
                        : 'bg-rose-50/90 border-rose-300 text-rose-950'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate min-w-0">
                      <span
                        className={`w-3.5 h-3.5 rounded-full shrink-0 flex items-center justify-center text-[9px] font-black ${
                          isPassed
                            ? 'bg-emerald-500 text-white shadow-[0_0_6px_#10b981]'
                            : 'bg-rose-500 text-white shadow-[0_0_6px_#f43f5e]'
                        }`}
                      >
                        {isPassed ? '✓' : '✕'}
                      </span>
                      <span className="font-bold truncate">{word.kab}</span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className="text-[11px] text-stone-600 italic truncate max-w-[120px]">{word.fr}</span>
                      <button
                        type="button"
                        onClick={() => playTTS(word.kab)}
                        className="p-1 rounded bg-white hover:bg-amber-100 border border-stone-200 text-stone-700 cursor-pointer"
                        title="Écouter"
                      >
                        🔊
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 w-full my-0.5">
            <div className="bg-[#fffdf9] border-2 border-amber-400/80 p-2.5 rounded-xl shadow-xs">
              <span className="text-[10px] text-amber-900 uppercase font-black block">XP Gagné</span>
              <span className="text-lg font-black text-amber-950">+{score}</span>
            </div>
            <div className="bg-[#fffdf9] border-2 border-amber-400/80 p-2.5 rounded-xl shadow-xs">
              <span className="text-[10px] text-amber-900 uppercase font-black block">Meilleure Série</span>
              <span className="text-lg font-black text-amber-950">{maxStreak}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full pt-1 pb-4">
            <button
              type="button"
              onClick={handleStartQuiz}
              className="flex-1 py-3 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-stone-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-xs active:scale-98 transition-all cursor-pointer flex items-center justify-center gap-1.5"
            >
              <span>🔄</span>
              <span>Continuer / Rejouer</span>
            </button>
            <button
              type="button"
              onClick={() => {
                if (challengeId) {
                  clearActiveQuizId(challengeId);
                  try {
                    localStorage.removeItem('tamazight_active_quiz_state_' + challengeId);
                  } catch {}
                }
                setStep('CONFIG');
              }}
              className="py-3 px-3 bg-amber-50 hover:bg-amber-100 text-amber-900 font-bold text-xs uppercase tracking-wider rounded-xl border border-amber-300 shadow-xs active:scale-98 transition-all cursor-pointer flex items-center justify-center gap-1"
            >
              <span>⚙️</span>
              <span>Paramètres</span>
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="py-3 px-4 bg-[#fffdf9] hover:bg-[#fef3c7] text-stone-800 font-bold text-xs uppercase tracking-wider rounded-xl border-2 border-stone-300 shadow-xs active:scale-98 transition-all cursor-pointer"
            >
              Terminer
            </button>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* VIEW 4: GAME OVER (HEARTS DEPLETED)                       */}
      {/* ========================================================= */}
      {step === 'GAMEOVER' && (
        <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center text-center gap-4 max-w-md w-full mx-auto">
          <div className="text-5xl sm:text-6xl animate-pulse">💔</div>
          <h2 className="text-2xl sm:text-3xl font-black text-red-700 uppercase tracking-wider font-cinzel">
            Plus de Cœurs !
          </h2>
          <p className="text-sm text-stone-700 max-w-xs leading-relaxed">
            Ne baissez pas les bras ! Révisez vos mots et recommencez pour transformer toutes les pastilles en vert.
          </p>

          <div className="flex gap-2 w-full pt-2">
            <button
              type="button"
              onClick={handleStartQuiz}
              className="flex-1 py-3 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-stone-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-xs active:scale-98 transition-all cursor-pointer flex items-center justify-center gap-1.5"
            >
              <span>🔄</span>
              <span>Réessayer</span>
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 py-3 bg-[#fffdf9] hover:bg-[#fef3c7] text-stone-800 font-bold text-xs uppercase tracking-wider rounded-xl border-2 border-stone-300 shadow-xs active:scale-98 transition-all cursor-pointer"
            >
              Quitter
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
